/**
 * GoogleSearchAdMockup Component
 *
 * Google Search ad mockup (RSA format)
 * Matches Google's current search results appearance
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { GoogleSearchAdMockupProps } from '../types/platform.types';

export const GoogleSearchAdMockup: React.FC<GoogleSearchAdMockupProps> = ({
  headlines,
  descriptions,
  finalUrl,
  displayPath = '',
  sitelinks = [],
}) => {
  // Google shows up to 3 headlines and 2 descriptions
  const displayHeadlines = headlines.slice(0, 3).map(h => stripMediaTags(h));
  const displayDescriptions = descriptions.slice(0, 2).map(d => stripMediaTags(d));

  // Extract hostname from URL
  let hostname = 'yourbrand.com';
  try {
    hostname = new URL(finalUrl).hostname;
  } catch (e) {
    // Keep default if URL parsing fails
  }

  return (
    <AdMockupContainer platform="google_search">
      <div className="bg-white dark:bg-background-dark rounded-lg p-4 max-w-2xl mx-auto">

        {/* Ad Badge and Breadcrumb */}
        <div className="flex items-center gap-2 mb-2">
          <span className="px-1.5 py-0.5 bg-white dark:bg-card-darkAlt border border-gray-300 dark:border-border-dark rounded text-xs font-medium text-gray-700 dark:text-gray-300">
            Ad
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">·</span>
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {hostname}{displayPath ? ` › ${displayPath}` : ''}
          </span>
        </div>

        {/* Headline (Blue, clickable appearance) */}
        {displayHeadlines.length > 0 && (
          <h3 className="text-xl text-primary mb-1 hover:underline cursor-pointer font-normal">
            {displayHeadlines.join(' | ')}
          </h3>
        )}

        {/* Display URL (Green) */}
        <div className="flex items-center gap-1 mb-2">
          <span className="text-sm text-green-700 dark:text-green-500">
            {hostname}
          </span>
          {displayPath && (
            <>
              <span className="text-gray-400 text-sm">›</span>
              <span className="text-sm text-green-700 dark:text-green-500">
                {displayPath}
              </span>
            </>
          )}
        </div>

        {/* Description */}
        {displayDescriptions.length > 0 && (
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            {displayDescriptions.join(' ')}
          </div>
        )}

        {/* Sitelinks (if available) */}
        {sitelinks.length > 0 && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-3 border-t border-gray-200 dark:border-border-dark">
            {sitelinks.slice(0, 4).map((link, idx) => (
              <div key={idx}>
                <p className="text-sm text-primary hover:underline cursor-pointer mb-0.5">
                  {link.title}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                  {link.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdMockupContainer>
  );
};

export default GoogleSearchAdMockup;
